import React, { useState, useEffect, useRef } from 'react';
import { useSetCurrentPage } from '../stores/useAppStore';
import { useStockMonitorStore } from '../stores/useStockMonitorStore';
import GradientBackground from '../components/ui/GradientBackground';
import ParticleBackground from '../components/ui/ParticleBackground';
import ConfirmDialog from '../components/ConfirmDialog';
import { klineDataService } from '../services/klineDataService';
import { stockMonitorService, StockMonitorTask } from '../services/stockMonitorService';
import { estimateTokenCount } from '../components/MyMonitors/utils';
import ContentDialog from '../components/ContentDialog';

import { useToastStore } from '../stores/useToastStore';
import { adminService, SystemPromptInfo } from '../services/adminService';
import { useEffectOnce } from '../hooks/useEffectOnce';

// Components
import MonitorCard from '../components/StockMonitors/MonitorCard';
import FilterBar from '../components/StockMonitors/FilterBar';
import MonitorStatusHeader from '../components/StockMonitors/MonitorStatusHeader';
import PageHeader from '../components/PageHeader';
import StockResearchDialog from '../components/StockMonitors/StockResearchDialog';
import { MonitorFilterStatus, type BarInterval } from '@/services/enums';

type StatusFilter = MonitorFilterStatus;

const StockMonitors: React.FC = () => {
  // 页面状态
  const [symbolFilter, setSymbolFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [loading, setLoading] = useState(true);
  const [batchOperating, setBatchOperating] = useState(false);
  const [systemPrompts, setSystemPrompts] = useState<SystemPromptInfo[]>([]);
  const [loadingPrompts, setLoadingPrompts] = useState(false);

  // Store hooks
  const setCurrentPage = useSetCurrentPage();
  const {
    monitors,
    totalMonitors,
    runningCount,
    currentPage,
    pageSize,
    isLoading,
    refreshMonitors,
    stopMonitor,
    startMonitor,
    stoppingMonitors,
    startingMonitors,
    deleteStoppedMonitors,
    silentRefreshMonitors,
    syncResearchTasks,
    getResearchStatus,
    setPage,
    isSyncingResearch,
    pipelineEnabled,
    getPipelineStatus,
    togglePipelineStatus,
  } = useStockMonitorStore();

  const showToast = useToastStore((state) => state.show);
  const hasInitializedRef = useRef(false);
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const silentRefreshIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // 批量删除确认对话框状态
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [stoppedCount, setStoppedCount] = useState(0);

  // 内容展示弹窗状态
  const [dialogVisible, setDialogVisible] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogContent, setDialogContent] = useState('');
  const [loadingContent, setLoadingContent] = useState(false);
  const [dialogTokenCount, setDialogTokenCount] = useState<number | undefined>(undefined);
  const [dialogSystemPrompt, setDialogSystemPrompt] = useState<string | undefined>(undefined);
  const [dialogUserPrompt, setDialogUserPrompt] = useState<string | undefined>(undefined);
  const [dialogUseMarkdown, setDialogUseMarkdown] = useState(false);
  const [dialogMonitorInfo, setDialogMonitorInfo] = useState<StockMonitorTask | null>(null);

  // 研报同步弹框状态
  const [showResearchDialog, setShowResearchDialog] = useState(false);

  // 初始化管道状态
  useEffectOnce(() => {
    getPipelineStatus();
  });

  // 加载系统提示词列表
  useEffectOnce(() => {
    const loadSystemPrompts = async () => {
      try {
        setLoadingPrompts(true);
        const result = await adminService.getSystemPrompts(1, 100, undefined, true);
        setSystemPrompts(result.prompts);
      } catch (error: any) {
        console.error('加载系统提示词列表失败:', error);
      } finally {
        setLoadingPrompts(false);
      }
    };
    loadSystemPrompts();
  });

  // 检查研报服务状态
  const refreshAutoMonitorStatus = async () => {
    try {
      await getResearchStatus();
    } catch (error) {
      console.error('刷新研报状态失败:', error);
    }
  };

  // 初始加载
  useEffectOnce(() => {
    hasInitializedRef.current = true;
    setCurrentPage('stock-monitors'); // 设置当前页面
    refreshMonitors(undefined, undefined, 1).finally(() => {
      setLoading(false);
    });
    refreshAutoMonitorStatus();
  });

  // 分页变化处理
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    const filterValue = symbolFilter.trim() || undefined;
    const statusValue = statusFilter === 'all' ? undefined : statusFilter;
    refreshMonitors(filterValue, statusValue, newPage);
    // 滚动到顶部
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 静默刷新逻辑
  useEffect(() => {
    const startSilentRefresh = () => {
      const filterValue = symbolFilter.trim() || undefined;
      const statusValue = statusFilter === 'all' ? undefined : statusFilter;
      silentRefreshMonitors(filterValue, statusValue).catch(() => {});

      // 刷新研报状态
      refreshAutoMonitorStatus();
    };

    if (hasInitializedRef.current) {
      const initialTimeout = setTimeout(() => {
        startSilentRefresh();
      }, 5000);

      // 每30秒刷新一次
      const interval = 30000;
      silentRefreshIntervalRef.current = setInterval(startSilentRefresh, interval);

      return () => {
        clearTimeout(initialTimeout);
        if (silentRefreshIntervalRef.current) {
          clearInterval(silentRefreshIntervalRef.current);
          silentRefreshIntervalRef.current = null;
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbolFilter, statusFilter, silentRefreshMonitors]);

  // 筛选防抖
  useEffect(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (hasInitializedRef.current) {
      debounceTimerRef.current = setTimeout(() => {
        const filterValue = symbolFilter.trim() || undefined;
        const statusValue = statusFilter === 'all' ? undefined : statusFilter;
        refreshMonitors(filterValue, statusValue, 1); // 筛选变动时重置到第一页
      }, 500);
    }

    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbolFilter, refreshMonitors]);

  // 状态筛选直接刷新
  useEffect(() => {
    if (hasInitializedRef.current) {
      const filterValue = symbolFilter.trim() || undefined;
      const statusValue = statusFilter === 'all' ? undefined : statusFilter;
      refreshMonitors(filterValue, statusValue, 1); // 筛选变动时重置到第一页
    }
  }, [statusFilter, symbolFilter, refreshMonitors]);

  // 处理停止监控
  const handleStopMonitor = async (monitorId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    await stopMonitor(monitorId);
  };

  // 处理启动监控
  const handleStartMonitor = async (monitorId: string, symbol: string, bar: BarInterval, e: React.MouseEvent) => {
    e.stopPropagation();
    await startMonitor(monitorId, symbol, bar);
  };

  // 批量启动所有停止的监控
  const handleBatchStart = async () => {
    const stoppedMonitors = monitors.filter((m) => m.status === 'stopped');
    if (stoppedMonitors.length === 0) return;

    setBatchOperating(true);
    let successCount = 0;
    let failCount = 0;

    for (const monitor of stoppedMonitors) {
      try {
        await startMonitor(monitor.monitorId, monitor.symbol, monitor.bar);
        successCount++;
        await new Promise((resolve) => setTimeout(resolve, 200));
      } catch (error: any) {
        failCount++;
        console.error(`批量启动监控失败: ${monitor.symbol} ${monitor.bar}`, error);
      }
    }

    setBatchOperating(false);
    if (failCount === 0) {
      showToast(`成功启动 ${successCount} 个监控任务`, 'success');
    } else {
      showToast(
        `启动完成：成功 ${successCount} 个，失败 ${failCount} 个`,
        failCount === stoppedMonitors.length ? 'error' : 'warning',
      );
    }
  };

  // 批量停止所有运行的监控
  const handleBatchStop = async () => {
    const runningMonitors = monitors.filter((m) => m.status === 'running');
    if (runningMonitors.length === 0) return;

    setBatchOperating(true);
    let successCount = 0;
    let failCount = 0;

    for (const monitor of runningMonitors) {
      try {
        await stopMonitor(monitor.monitorId);
        successCount++;
        await new Promise((resolve) => setTimeout(resolve, 200));
      } catch (error: any) {
        failCount++;
        console.error(`批量停止监控失败: ${monitor.symbol} ${monitor.bar}`, error);
      }
    }

    setBatchOperating(false);
    if (failCount === 0) {
      showToast(`成功停止 ${successCount} 个监控任务`, 'success');
    } else {
      showToast(
        `停止完成：成功 ${successCount} 个，失败 ${failCount} 个`,
        failCount === runningMonitors.length ? 'error' : 'warning',
      );
    }
  };

  // 批量删除
  const handleBatchDeleteClick = () => {
    const count = monitors.filter((m) => m.status === 'stopped').length;
    if (count === 0) return;
    setStoppedCount(count);
    setShowDeleteConfirm(true);
  };

  const handleBatchDeleteConfirm = async () => {
    setShowDeleteConfirm(false);
    setBatchOperating(true);
    try {
      await deleteStoppedMonitors();
      await refreshMonitors();
    } catch (error: any) {
      console.error('批量删除监控失败:', error);
    } finally {
      setBatchOperating(false);
    }
  };

  // 查看提示词
  const handleViewPrompt = async (monitor: StockMonitorTask, e: React.MouseEvent) => {
    e.stopPropagation();

    // 优先使用直接返回的字段
    if (monitor.systemPromptText || monitor.userPromptText) {
      setDialogVisible(true);
      setDialogTitle('提示词');
      setDialogUseMarkdown(false);
      setDialogMonitorInfo(monitor);

      const systemPrompt = monitor.systemPromptText || '';
      const userPrompt = monitor.userPromptText || '';
      setDialogSystemPrompt(systemPrompt);
      setDialogUserPrompt(userPrompt);

      const fullPrompt = systemPrompt ? `${systemPrompt}\n\n---\n\n${userPrompt}` : userPrompt;
      setDialogContent(fullPrompt);
      const tokenCount = estimateTokenCount(fullPrompt);
      setDialogTokenCount(tokenCount);
      return;
    }

    // 如果没有直接字段，尝试通过 promptHash 获取 (兼容旧数据)
    if (!monitor.promptHash) {
      showToast('该监控任务没有提示词', 'error');
      return;
    }

    setLoadingContent(true);
    setDialogVisible(true);
    setDialogTitle('提示词');
    setDialogContent('加载中...');
    setDialogUseMarkdown(false);
    setDialogTokenCount(undefined);
    setDialogMonitorInfo(monitor);

    try {
      const result = await stockMonitorService.getPrompt(monitor.promptHash, monitor.bar);
      setDialogSystemPrompt(result.systemPrompt);
      setDialogUserPrompt(result.userPrompt);
      const fullPrompt = result.systemPrompt
        ? `${result.systemPrompt}\n\n---\n\n${result.userPrompt}`
        : result.userPrompt;
      setDialogContent(fullPrompt);
      const tokenCount = estimateTokenCount(fullPrompt);
      setDialogTokenCount(tokenCount);
    } catch (error: any) {
      showToast(error.message || '获取提示词失败', 'error');
      setDialogVisible(false);
      setDialogMonitorInfo(null);
    } finally {
      setLoadingContent(false);
    }
  };

  // 查看分析报告
  const handleViewReport = async (monitor: StockMonitorTask, e: React.MouseEvent) => {
    e.stopPropagation();

    // 优先使用直接返回的字段
    if (monitor.analysisDescription) {
      setDialogVisible(true);
      setDialogTitle('分析报告');
      setDialogContent(monitor.analysisDescription);
      setDialogUseMarkdown(true);
      setDialogTokenCount(estimateTokenCount(monitor.analysisDescription));
      setDialogSystemPrompt(undefined);
      setDialogUserPrompt(undefined);
      setDialogMonitorInfo(null);
      return;
    }

    // 如果没有直接字段，尝试通过 hash 获取 (兼容旧数据)
    if (!monitor.analysisReportHash) {
      showToast('该监控任务没有分析报告', 'error');
      return;
    }

    setLoadingContent(true);
    setDialogVisible(true);
    setDialogTitle('分析报告');
    setDialogContent('加载中...');
    setDialogUseMarkdown(true);
    setDialogTokenCount(undefined);
    setDialogSystemPrompt(undefined);
    setDialogUserPrompt(undefined);
    setDialogMonitorInfo(null);

    try {
      const result = await klineDataService.getAnalysisReport(monitor.analysisReportHash);
      setDialogContent(result.analysisReport);
      const tokenCount = estimateTokenCount(result.analysisReport);
      setDialogTokenCount(tokenCount);
    } catch (error: any) {
      showToast(error.message || '获取分析报告失败', 'error');
      setDialogVisible(false);
    } finally {
      setLoadingContent(false);
    }
  };

  // 点击卡片
  const handleCardClick = (monitor: any) => {
    // 暂时无操作，或者打开详情页
    console.log('Clicked monitor:', monitor);
  };

  // 点击研报同步按钮
  const handleResearchClick = () => {
    setShowResearchDialog(true);
  };

  // 确认启动研报同步
  const handleConfirmResearchSync = async (config: {
    minAmount?: number;
    monitorCount: number;
    strategyIds: string[];
    excludedSymbols: string[];
    systemPromptId?: string;
    filterAnalyzed: boolean;
    symbolKeyword?: string;
  }) => {
    try {
      await syncResearchTasks(
        config.monitorCount,
        config.excludedSymbols,
        config.strategyIds,
        config.minAmount,
        config.filterAnalyzed,
        config.symbolKeyword,
      );
      // Toast message is now handled in store
      setShowResearchDialog(false);
      // 同步启动后刷新列表
      setTimeout(() => refreshMonitors(), 2000);
    } catch (error: any) {
      // 错误已在 store 中处理
    }
  };

  return (
    <GradientBackground className="text-white">
      <ParticleBackground />

      <div className="fixed inset-0 flex flex-col overflow-hidden">
        {/* 顶部标题栏 (Fixed) */}
        <PageHeader
          title="A股智能监控"
          onBack={() => setCurrentPage('stock-dashboard')}
          rightContent={
            <button
              onClick={() => {
                refreshMonitors(symbolFilter.trim() || undefined, statusFilter === 'all' ? undefined : statusFilter);
                refreshAutoMonitorStatus();
              }}
              className="flex items-center justify-center w-9 h-9 bg-white/10 backdrop-blur-sm border border-white/20 rounded-xl text-white hover:bg-white/20 hover:border-white/30 transition-all duration-300 hover:scale-105"
              aria-label="刷新"
              title="刷新监控列表"
            >
              <svg
                className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
            </button>
          }
        />

        <FilterBar
          symbolFilter={symbolFilter}
          setSymbolFilter={setSymbolFilter}
          statusFilter={statusFilter}
          setStatusFilter={setStatusFilter}
          onResearchClick={handleResearchClick}
          researchLoading={isSyncingResearch}
          pipelineEnabled={pipelineEnabled}
          onTogglePipeline={() => togglePipelineStatus()}
          runningCount={runningCount}
          totalMonitors={totalMonitors}
        />

        {/* 主体内容区域 - 响应式布局 */}
        <div className="flex-1 flex flex-col min-h-0 pt-[148px]">
          {/* 批量操作 (相对固定在列表上方) */}
          <div className="px-4 py-1 z-30 border-b border-white/5">
            <MonitorStatusHeader
              monitors={monitors}
              isLoading={isLoading}
              batchOperating={batchOperating}
              startingMonitors={startingMonitors}
              stoppingMonitors={stoppingMonitors}
              onBatchStart={handleBatchStart}
              onBatchStop={handleBatchStop}
              onBatchDelete={handleBatchDeleteClick}
            />
          </div>

          {/* 监控列表 - 独立滚动 */}
          <div className="flex-1 overflow-y-auto px-4 pb-2 custom-scrollbar">
            <div className="space-y-3 pt-3">
              {(loading || isLoading) && monitors.length === 0 ? (
                <div className="text-center py-20 text-white/50">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white/50 mx-auto mb-4"></div>
                  加载中...
                </div>
              ) : monitors.length === 0 ? (
                <div className="text-center py-20 text-white/50">
                  <p>暂无监控任务</p>
                </div>
              ) : (
                <>
                  {monitors.map((monitor) => (
                    <MonitorCard
                      key={monitor.monitorId}
                      monitor={monitor}
                      isStopping={stoppingMonitors.has(monitor.monitorId)}
                      isStarting={startingMonitors.has(monitor.monitorId)}
                      onStop={handleStopMonitor}
                      onStart={handleStartMonitor}
                      onViewPrompt={handleViewPrompt}
                      onViewReport={handleViewReport}
                      onClick={handleCardClick}
                    />
                  ))}
                </>
              )}
            </div>
          </div>

          {/* 分页控制 - 固定底部 */}
          {totalMonitors > pageSize && (
            <div className="flex-none px-4 py-2 bg-black/40 backdrop-blur-md border-t border-white/10 shadow-[0_-4px_20px_rgba(0,0,0,0.5)]">
              <div className="flex items-center justify-center space-x-2">
                <button
                  onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1 || isLoading}
                  className="px-4 py-1.5 rounded-lg bg-white/10 text-white/70 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition-all active:scale-95 text-sm"
                >
                  上一页
                </button>
                <div className="flex items-center px-4 py-1.5 rounded-lg bg-white/5 text-white/90 border border-white/10">
                  <span className="text-xs font-medium">
                    第 {currentPage} 页 / 共 {Math.ceil(totalMonitors / pageSize)} 页
                  </span>
                </div>
                <button
                  onClick={() => handlePageChange(Math.min(Math.ceil(totalMonitors / pageSize), currentPage + 1))}
                  disabled={currentPage >= Math.ceil(totalMonitors / pageSize) || isLoading}
                  className="px-4 py-1.5 rounded-lg bg-white/10 text-white/70 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed transition-all active:scale-95 text-sm"
                >
                  下一页
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 内容展示弹窗 */}
      <ContentDialog
        visible={dialogVisible}
        title={dialogTitle}
        content={dialogContent}
        loading={loadingContent}
        useMarkdown={dialogUseMarkdown}
        tokenCount={dialogTokenCount}
        onClose={() => setDialogVisible(false)}
        systemPrompt={dialogSystemPrompt}
        userPrompt={dialogUserPrompt}
        monitorInfo={
          dialogMonitorInfo
            ? {
                monitorId: dialogMonitorInfo.monitorId,
                symbol: dialogMonitorInfo.symbol,
                bar: dialogMonitorInfo.bar,
                promptHash: dialogMonitorInfo.promptHash,
              }
            : null
        }
      />

      {/* 批量删除确认对话框 */}
      <ConfirmDialog
        show={showDeleteConfirm}
        title="批量删除确认"
        message={`确定要删除所有已停止的监控任务吗？此操作将删除 ${stoppedCount} 个任务且不可恢复。`}
        onConfirm={handleBatchDeleteConfirm}
        onCancel={() => setShowDeleteConfirm(false)}
        confirmText="删除"
        cancelText="取消"
      />

      {/* 研报同步对话框 */}
      <StockResearchDialog
        isOpen={showResearchDialog}
        onClose={() => setShowResearchDialog(false)}
        onConfirm={handleConfirmResearchSync}
        loading={isSyncingResearch}
        systemPrompts={systemPrompts}
        loadingPrompts={loadingPrompts}
      />
    </GradientBackground>
  );
};

export default StockMonitors;
